import { useState } from 'react'
import { X, Trash2, Download, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

interface BulkActionsBarProps {
  selectedCount: number
  totalCount: number
  onSelectAll: () => void
  onClearSelection: () => void
  onDeleteSelected: () => Promise<void>
  onDownloadSelected?: () => Promise<void>
  isDeleting?: boolean
  isDownloading?: boolean
  className?: string
}

export function BulkActionsBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onDeleteSelected,
  onDownloadSelected,
  isDeleting = false,
  isDownloading = false,
  className,
}: BulkActionsBarProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  if (selectedCount === 0) return null

  const allSelected = selectedCount === totalCount && totalCount > 0

  const handleDelete = async () => {
    setShowDeleteDialog(false)
    await onDeleteSelected()
  }

  return (
    <>
      <div
        className={cn(
          'flex items-center justify-between gap-4 p-3 bg-accent/10 border border-accent rounded-lg animate-in slide-in-from-top-2 duration-200',
          className
        )}
      >
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClearSelection}
            title="Cancelar selección"
          >
            <X className="h-4 w-4" />
          </Button>

          <span className="text-sm font-medium">
            {selectedCount} {selectedCount === 1 ? 'documento seleccionado' : 'documentos seleccionados'}
          </span>

          {!allSelected && (
            <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onSelectAll}>
              Seleccionar todos ({totalCount})
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onDownloadSelected && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDownloadSelected}
              disabled={isDownloading || isDeleting}
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Descargar
            </Button>
          )}

          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            disabled={isDeleting || isDownloading}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Eliminar
          </Button>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar documentos</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar {selectedCount}{' '}
              {selectedCount === 1 ? 'documento' : 'documentos'}? Esta acción no se puede deshacer y
              los archivos serán eliminados permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar {selectedCount} {selectedCount === 1 ? 'documento' : 'documentos'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
